import React from 'react';
import { useSelector } from 'react-redux';
import { Header } from '../styled';
import { Tooltip, message, Modal } from 'antd';
import { SettingOutlined, UndoOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  AdminContainer,
  SectionContainer,
  SectionHeader,
  SectionContent,
  AdminButton,
  InfoBox,
} from './styled';
import { persistenceResponseSelector } from '@store/persistentConfig/selectors';
import { useCustomerLevelDefaultsConfig } from '@hooks/useCustomerLevelPersistence';
import { useCustomerLevelDefaultLandingPageConfig } from '@hooks/useCustomerLevelDefaultLandingPage';
import { useCustomerDefaultLandingPageInfo } from '@hooks/useCustomerDefaultLandingPageInfo';
import { getPersistentHashKey, validateAndExtractConfiguration } from '@utils/persistenceUtil';
import { NOC_DRILLDOWN } from '@src/utils/constants';
import { modeSelector } from '@src/store/trends/selectors';
import { MODE_AI_ALERT_DIAGNOSTICS } from '@src/store/trends/constants';
import { useAtomValue } from 'jotai';
import { pageMetaAtom } from '@src/atoms/pageMeta';

const AdminControls: React.FC = () => {
  const shouldDisplayReset = window.location.pathname.includes('preset');
  const isAppExperience = window.location.pathname.endsWith(`/dashboard/app-experience`);
  const { isSharing, isPublicSavedDashboard, isSavedDashboard } = useAtomValue(pageMetaAtom);
  const { mode } = useSelector(modeSelector);
  const persistence = useSelector(persistenceResponseSelector);

  // Hide default landing page options for certain modes and sharing pages (similar to Actions.tsx)
  const hideLandingPageOptions =
    isSharing || [MODE_AI_ALERT_DIAGNOSTICS, NOC_DRILLDOWN].includes(mode);

  // For public saved dashboards, only show landing page options (no persistence options)
  const isPublicSavedDashboardPage = isSavedDashboard && isPublicSavedDashboard;

  const { applyToAll, removeCustomerDefault, isSettingDefault, isDeletingDefault } =
    useCustomerLevelDefaultsConfig();

  const {
    applyToAll: applyLandingPageToAll,
    removeCustomerDefault: removeLandingPageDefault,
    isSettingDefault: isSavingDefaultLandingPage,
    isDeletingDefault: isDeletingDefaultLandingPage,
  } = useCustomerLevelDefaultLandingPageConfig();

  const { customerDefaultInfo, refetch: refetchCustomerDefault } =
    useCustomerDefaultLandingPageInfo();

  // Check if current page is the customer default landing page
  const { pathname } = window.location;
  const isCurrentPageCustomerDefault =
    customerDefaultInfo?.exists &&
    customerDefaultInfo?.path &&
    customerDefaultInfo.path === pathname;

  const handleApplyToAll = () => {
    Modal.confirm({
      title: 'Apply Configuration to All Users',
      content:
        'This will set the current configuration as the default for all first-time users in this account. Users with existing personalized settings will not be affected by this change.',
      okText: 'Apply to All',
      cancelText: 'Cancel',
      onOk: () => {
        const currentKey = getPersistentHashKey();
        const validation = validateAndExtractConfiguration(persistence, currentKey);

        if (!validation.valid) {
          message.error(validation.error);
          return;
        }

        applyToAll(validation.configuration, currentKey);
      },
    });
  };

  const handleRemoveDefault = () => {
    Modal.confirm({
      title: 'Revert to Conviva Default',
      content:
        "This will remove the customer default configuration. Users without personalized settings will see Conviva's system default instead.",
      okText: 'Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: () => {
        const currentKey = getPersistentHashKey();
        if (!currentKey) {
          message.error('Unable to determine persistence key');
          return;
        }
        removeCustomerDefault(currentKey);
      },
    });
  };

  const handleSaveDefaultLandingPageForAll = () => {
    Modal.confirm({
      title: 'Set Default Landing Page for All Users',
      content:
        'This will set the current page as the default landing page for all first-time users in this account. Users with existing personalized landing pages will not be affected by this change.',
      okText: 'Set as Default',
      cancelText: 'Cancel',
      onOk: async () => {
        await applyLandingPageToAll();
        refetchCustomerDefault();
      },
    });
  };
  const handleRemoveDefaultLandingPage = () => {
    Modal.confirm({
      title: 'Remove Default Landing Page',
      content:
        'This will remove the customer default landing page. Users without personalized landing pages will see the system default instead.',
      okText: 'Remove',
      cancelText: 'Cancel',
      okType: 'danger',
      onOk: async () => {
        await removeLandingPageDefault();
        refetchCustomerDefault();
      },
    });
  };

  return (
    <AdminContainer direction="vertical">
      <Header>Admin Controls</Header>
      {shouldDisplayReset || isAppExperience || isPublicSavedDashboardPage ? (
        <>
          {/* Persistence Section - hidden for public saved dashboards */}
          {!isPublicSavedDashboardPage && (
            <SectionContainer>
              <SectionHeader>Persistence</SectionHeader>
              <SectionContent direction="vertical">
                <Tooltip title="Set current configuration as default for all users">
                  <AdminButton
                    type="text"
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={handleApplyToAll}
                    loading={isSettingDefault}
                  >
                    Apply to all
                  </AdminButton>
                </Tooltip>

                <Tooltip title="Remove customer default configuration for all users">
                  <AdminButton
                    type="text"
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={handleRemoveDefault}
                    loading={isDeletingDefault}
                  >
                    Remove for all
                  </AdminButton>
                </Tooltip>
              </SectionContent>
            </SectionContainer>
          )}
          {/* Landing Page Section */}
          {!hideLandingPageOptions && (
            <SectionContainer marginTop={isPublicSavedDashboardPage ? '0' : '16px'}>
              <SectionHeader>Default Landing Page</SectionHeader>

              {/* Show current customer default info */}
              {customerDefaultInfo?.exists && isCurrentPageCustomerDefault && (
                <InfoBox>
                  <InfoCircleOutlined style={{ margin: 4, color: '#667eea' }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>
                      Current default for all users
                    </div>
                  </div>
                </InfoBox>
              )}

              <SectionContent direction="vertical">
                <Tooltip title="Set this dashboard as default landing page for all">
                  <AdminButton
                    type="text"
                    size="small"
                    icon={<SettingOutlined />}
                    onClick={handleSaveDefaultLandingPageForAll}
                    loading={isSavingDefaultLandingPage}
                  >
                    Apply to all
                  </AdminButton>
                </Tooltip>

                <Tooltip title="Remove default landing page for all">
                  <AdminButton
                    type="text"
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={handleRemoveDefaultLandingPage}
                    loading={isDeletingDefaultLandingPage}
                    disabled={!customerDefaultInfo?.exists}
                  >
                    Remove for all
                  </AdminButton>
                </Tooltip>
              </SectionContent>
            </SectionContainer>
          )}
        </>
      ) : (
        <div>No admin actions available</div>
      )}
    </AdminContainer>
  );
};

export default AdminControls;
